import { useEffect, useState } from 'react';

import type { Message } from '@/features/chat/chat.api';
import { PENDING_DELIVERY_STATUS } from '@/features/chat/delivery-status';
import { requestReadReceipt } from '@/features/chat/read-receipts.api';

/**
 * The console's WhatsApp read-receipt trigger — 2026-09 F4, spec
 * `docs/specs/2026-09-features/04-two-way-read-sync.md` §3 (decided Q1) and
 * §9.
 *
 * Blue ticks reach a customer ONLY when an agent verifiably viewed the
 * conversation. Per member conversation shown in the view, ALL must hold
 * before `POST /conversations/{id}/read-receipt` is called:
 *
 *   1. the conversation (or the merged thread containing it) is open — this
 *      hook lives in `MessageList`, so "mounted" is "open";
 *   2. `document.visibilityState === 'visible'` AND `document.hasFocus()`,
 *      AND no modal dialog covers the page — the image lightbox, a confirm
 *      dialog… ({@link modalDialogOpen}): the window keeps its focus while
 *      one is open (the dialog takes it), and the list below keeps its
 *      stick-to-bottom flag;
 *   3. the message list is at the newest message (`isAtBottom` — the
 *      `MessageList` stick-to-bottom flag, within 120 px);
 *   4. real keyboard / mouse / wheel / touch input within the last 3 minutes;
 *   5. 1–4 held CONTINUOUSLY for a 2 s dwell, counted from when the newest
 *      inbound appeared in the view or from when 1–4 last became true,
 *      whichever is later. A newer inbound restarts that conversation's dwell,
 *      so a burst is reported once, after it settles.
 *
 * The hub decides whether anything is actually sent (mode `off` by default,
 * a one-number pilot allow-list, connected account, direct chat, age, the
 * claim watermark). The console's contract is only to CALL when the view
 * conditions hold, without ever producing retries or storms:
 *
 *   - `seen_through` = the server `created_at` of the conversation's NEWEST
 *     INBOUND message, verbatim. Outbound rows (the agent's reply, its
 *     delivery ticks) never make a conversation pending; optimistic rows are
 *     ignored outright.
 *   - At most ONE call in flight per conversation (page-wide); a newer inbound
 *     that arrives meanwhile is reported once that call settles.
 *   - Every answer settles its message — whatever the hub says, and on a
 *     failed call too, the same newest inbound is never sent again in this
 *     view. `mode: "off"` (= `skipped: "disabled"`) additionally parks the
 *     whole page for 10 minutes. The natural retry points are the next newer
 *     inbound and re-opening the conversation (a switch resets the trigger —
 *     the hub answers `nothing_new` for what is already claimed, and re-claims
 *     what a failed background RPC reverted).
 *   - Timers exist only while some conversation has an unreported newest
 *     inbound: a ≤1 s re-check (one wake-up per second at most, shortened to
 *     land exactly on a due dwell). While the tab is hidden NOTHING runs; the
 *     `visibilitychange` / `focus` / `blur` / `focusin` listeners and the
 *     first input after an idle stretch wake it up. During the `off`
 *     back-off there is a single wake-up at its end instead of the interval.
 *
 * Observe-only by construction: no React state (the hook never causes a
 * render — `MessageList`'s scroll state machine re-runs on every commit), and
 * it only READS `messages`, the `isAtBottom` probe and whether a modal dialog
 * is open.
 */

/** Condition 5 — all conditions held continuously for this long. */
const DWELL_MS = 2_000;

/** Condition 4 — input counts as "recent" for this long. */
const ACTIVITY_WINDOW_MS = 3 * 60_000;

/** Longest gap between two re-checks while something is unreported. */
const TICK_MS = 1_000;

/** Hub answered `mode: "off"` → no call from this page for this long. */
const MODE_OFF_BACKOFF_MS = 10 * 60_000;

/** Real input only. `scroll` is deliberately absent: `MessageList`'s own pin
 *  scrolls programmatically, which is not an agent looking at the screen
 *  (wheel / keyboard / pointer scrolling is covered by these). */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'] as const;

/** Capture phase on `window`: seen before any component can stop propagation
 *  (e.g. the editor's Escape handling); passive: never blocks scrolling. */
const ACTIVITY_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

/** Id prefix of an optimistic placeholder — `buildOptimisticMessage`
 *  (`useSendMessage.ts`) builds `optim-${clientRef}`. */
const OPTIMISTIC_ID_PREFIX = 'optim-';

/**
 * An open modal dialog, anywhere in the page. Every modal in the console is a
 * Radix Dialog — the image lightbox (`ImageLightbox`, PR #65/#66) and every
 * confirm / QR / settings dialog — whose content carries `role="dialog"` +
 * `data-state="open"` while open (and `"closed"` during its exit), portaled
 * with an overlay over the whole page. The same open-dialog test
 * `escape-guard.ts` uses; the non-modal emoji panel deliberately carries no
 * `data-state` and is not matched. There is no store signal to prefer: the
 * lightbox keeps its `open` flag in local component state.
 */
const OPEN_MODAL_DIALOG_SELECTOR =
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

/** Condition 2's modal half — read at decision time, like the others. */
function modalDialogOpen(): boolean {
  return document.querySelector(OPEN_MODAL_DIALOG_SELECTOR) !== null;
}

// --- Page-wide state (shared by every mounted trigger) ------------------------

/** Epoch ms of the last real input anywhere in the page. */
let lastActivityAt = Number.NEGATIVE_INFINITY;

/** Mounted triggers; the page-wide input listeners exist while > 0. */
let activityListenerUsers = 0;

/** Mounted triggers to wake on the first input after an idle stretch (the
 *  one activity transition no timer is watching for). */
const idleWakeSubscribers = new Set<() => void>();

/** Hub answered `mode: "off"` — no call before this epoch ms. Page-wide, so a
 *  remount (classic ↔ thread view) cannot shorten the back-off. */
let modeOffUntil = 0;

/** Conversations with a read-receipt call in flight. Page-wide for the same
 *  reason: at most ONE call per conversation, whichever view issued it. */
const inFlight = new Set<string>();

function recentlyActive(now: number): boolean {
  return now - lastActivityAt < ACTIVITY_WINDOW_MS;
}

function noteActivity(): void {
  const now = Date.now();
  const wasIdle = !recentlyActive(now);
  lastActivityAt = now;
  if (wasIdle) {
    for (const wake of [...idleWakeSubscribers]) wake();
  }
}

function retainActivityListeners(): void {
  activityListenerUsers += 1;
  if (activityListenerUsers !== 1) return;
  for (const type of ACTIVITY_EVENTS) {
    window.addEventListener(type, noteActivity, ACTIVITY_LISTENER_OPTIONS);
  }
}

function releaseActivityListeners(): void {
  activityListenerUsers = Math.max(0, activityListenerUsers - 1);
  if (activityListenerUsers !== 0) return;
  for (const type of ACTIVITY_EVENTS) {
    window.removeEventListener(type, noteActivity, ACTIVITY_LISTENER_OPTIONS);
  }
}

// --- What a view is showing ----------------------------------------------------

interface Candidate {
  /** Server id of the conversation's newest inbound message. */
  id: string;
  /** Its `created_at` exactly as served — sent as `seen_through`. */
  through: string;
  /** `created_at` parsed to epoch ms — for ordering only (sub-ms digits are
   *  dropped here, never on the wire). */
  ms: number;
}

interface PendingReceipt {
  candidate: Candidate;
  /** When this candidate first appeared in the view — the earliest possible
   *  start of its dwell. */
  arrivedAt: number;
}

function isOptimistic(message: Message): boolean {
  return (
    message.id.startsWith(OPTIMISTIC_ID_PREFIX) ||
    message.delivery_status === PENDING_DELIVERY_STATUS
  );
}

/** Newest REAL inbound message per conversation. By `created_at`, not by
 *  slot: a late realtime replay can prepend an older row to the cache. On a
 *  same-millisecond tie the later slot wins (`displayed` is oldest-first). */
function newestInboundByConversation(messages: readonly Message[]): Map<string, Candidate> {
  const newest = new Map<string, Candidate>();
  for (const message of messages) {
    if (message.direction !== 'inbound' || isOptimistic(message)) continue;
    const ms = Date.parse(message.created_at);
    if (!Number.isFinite(ms)) continue;
    const current = newest.get(message.conversation_id);
    if (current === undefined || ms >= current.ms) {
      newest.set(message.conversation_id, { id: message.id, through: message.created_at, ms });
    }
  }
  return newest;
}

/** Is `candidate` something not yet reported? A different message that is
 *  not older than the last reported one (identity, so two messages inside
 *  one millisecond still count as two). */
function isUnreported(candidate: Candidate, reported: Candidate | undefined): boolean {
  if (reported === undefined) return true;
  return candidate.id !== reported.id && candidate.ms >= reported.ms;
}

// --- One trigger per mounted view ----------------------------------------------

interface ReceiptTrigger {
  attach(): void;
  detach(): void;
  setAtBottomProbe(probe: () => boolean): void;
  showView(viewKey: string | null, messages: readonly Message[]): void;
}

function createReceiptTrigger(): ReceiptTrigger {
  let attached = false;
  let isAtBottom: () => boolean = () => false;
  /** `undefined` until the first `showView`, so the first view also resets. */
  let viewKey: string | null | undefined;
  /** Bumped by every view switch; a call answered for an older view must not
   *  write into the current view's bookkeeping. */
  let generation = 0;
  /** conversation id → its unreported newest inbound (the interval runs only
   *  while this is non-empty). */
  let pending = new Map<string, PendingReceipt>();
  /** conversation id → the newest inbound already settled in this view (the
   *  spec's per-conversation `lastReportedThrough`). */
  let reported = new Map<string, Candidate>();
  /** Since when 1–4 have held without interruption; null = not holding. */
  let conditionsSince: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function wakeIn(ms: number): void {
    timer = setTimeout(() => {
      timer = null;
      evaluate();
    }, Math.max(0, ms));
  }

  /** The single decision point — re-run on every data change, every
   *  visibility / focus change, the first input after an idle stretch, every
   *  timer and every settled call. Leaves at most ONE timer behind. */
  function evaluate(): void {
    clearTimer();
    if (!attached) return;
    if (document.visibilityState !== 'visible' || pending.size === 0) {
      // Hidden tab: stop entirely. Nothing unreported: nothing to watch.
      conditionsSince = null;
      return;
    }
    const now = Date.now();
    if (now < modeOffUntil) {
      // The hub is off: ONE wake-up at the back-off's end, and a fresh dwell
      // after it — nothing was being watched in between.
      conditionsSince = null;
      wakeIn(modeOffUntil - now);
      return;
    }
    if (!document.hasFocus() || modalDialogOpen() || !recentlyActive(now) || !isAtBottom()) {
      conditionsSince = null;
      wakeIn(TICK_MS);
      return;
    }
    if (conditionsSince === null) conditionsSince = now;
    let nextCheckIn = TICK_MS;
    for (const [conversationId, entry] of pending) {
      if (inFlight.has(conversationId)) continue; // coalesced behind the call in flight
      const dueAt = Math.max(conditionsSince, entry.arrivedAt) + DWELL_MS;
      if (now >= dueAt) {
        send(conversationId, entry.candidate);
      } else {
        nextCheckIn = Math.min(nextCheckIn, dueAt - now);
      }
    }
    // Still unreported (dwelling, or answered not yet): keep watching.
    wakeIn(nextCheckIn);
  }

  function send(conversationId: string, candidate: Candidate): void {
    inFlight.add(conversationId);
    const sentIn = generation;
    requestReadReceipt(conversationId, candidate.through)
      .then(
        (result) => {
          if (result.mode === 'off' || result.skipped === 'disabled') {
            modeOffUntil = Date.now() + MODE_OFF_BACKOFF_MS;
          }
        },
        (error: unknown) => {
          // Best effort, never retried for the same message (see the module
          // doc for the natural retry points). One line per failed call — the
          // call rate is bounded by inbound messages actually viewed.
          console.warn('[read-receipts] read-receipt request failed', error);
        },
      )
      .finally(() => {
        inFlight.delete(conversationId);
        settle(conversationId, candidate, sentIn);
        evaluate();
      });
  }

  /** Whatever the answer, `candidate` is done for this view. */
  function settle(conversationId: string, candidate: Candidate, sentIn: number): void {
    if (sentIn !== generation) return; // the view switched; its state was reset
    if (isUnreported(candidate, reported.get(conversationId))) {
      reported.set(conversationId, candidate);
    }
    // A newer inbound that arrived meanwhile stays pending.
    if (pending.get(conversationId)?.candidate.id === candidate.id) {
      pending.delete(conversationId);
    }
  }

  return {
    attach(): void {
      if (attached) return;
      attached = true;
      retainActivityListeners();
      // The click / key press that opened this view is real input the page
      // listeners may have missed (none exist while no conversation is open):
      // transient user activation is only ever granted by such input, and
      // expires within seconds.
      if (navigator.userActivation?.isActive === true) noteActivity();
      idleWakeSubscribers.add(evaluate);
      document.addEventListener('visibilitychange', evaluate);
      // `blur` re-evaluates at once (`hasFocus()` is already false), so a
      // focus loss breaks the dwell even if it ends before the next re-check.
      window.addEventListener('focus', evaluate);
      window.addEventListener('blur', evaluate);
      // Same for a modal dialog: opening one moves focus into it (Radix
      // auto-focuses its content), so the dwell breaks at once even if the
      // dialog closes again before the next re-check. Its close needs no
      // listener — the next ≤ 1 s re-check starts a fresh dwell.
      document.addEventListener('focusin', evaluate);
      evaluate();
    },

    detach(): void {
      if (!attached) return;
      attached = false;
      clearTimer();
      conditionsSince = null;
      idleWakeSubscribers.delete(evaluate);
      document.removeEventListener('visibilitychange', evaluate);
      window.removeEventListener('focus', evaluate);
      window.removeEventListener('blur', evaluate);
      document.removeEventListener('focusin', evaluate);
      releaseActivityListeners();
    },

    setAtBottomProbe(probe: () => boolean): void {
      isAtBottom = probe;
    },

    showView(nextViewKey: string | null, messages: readonly Message[]): void {
      if (nextViewKey !== viewKey) {
        // Conversation / thread switch: start over — a fresh dwell for what
        // the new view shows, nothing carried over from the old one.
        viewKey = nextViewKey;
        generation += 1;
        pending = new Map();
        reported = new Map();
        conditionsSince = null;
      }
      const now = Date.now();
      const next = new Map<string, PendingReceipt>();
      for (const [conversationId, candidate] of newestInboundByConversation(messages)) {
        if (!isUnreported(candidate, reported.get(conversationId))) continue;
        const existing = pending.get(conversationId);
        next.set(
          conversationId,
          existing !== undefined && existing.candidate.id === candidate.id
            ? existing
            : { candidate, arrivedAt: now },
        );
      }
      pending = next;
      evaluate();
    },
  };
}

export interface UseReadReceiptTriggerOptions {
  /** The view's messages, oldest-first — `MessageList`'s de-duplicated
   *  `displayed` (one conversation, or every member of a merged thread). */
  messages: readonly Message[];
  /** Live "scrolled to the newest message" probe, read at decision time. */
  isAtBottom: () => boolean;
  /** Identity of the open view (thread key or conversation id); a change is a
   *  conversation / thread switch and resets the trigger. */
  viewKey: string | null;
}

/**
 * Mount inside the component that renders a conversation's message list.
 * Renders nothing and returns nothing; see the module doc for the rules.
 */
export function useReadReceiptTrigger({
  messages,
  isAtBottom,
  viewKey,
}: UseReadReceiptTriggerOptions): void {
  const [trigger] = useState(createReceiptTrigger);

  // Page listeners and timers live exactly as long as the list is mounted.
  useEffect(() => {
    trigger.attach();
    return () => trigger.detach();
  }, [trigger]);

  // Declared before the view effect so a render that changes both hands the
  // view evaluation the latest probe.
  useEffect(() => {
    trigger.setAtBottomProbe(isAtBottom);
  }, [trigger, isAtBottom]);

  useEffect(() => {
    trigger.showView(viewKey, messages);
  }, [trigger, viewKey, messages]);
}

/** Test-only: reset the page-wide state between tests (the listeners follow
 *  mount / unmount and need no reset). */
export const __testing = {
  reset(): void {
    lastActivityAt = Number.NEGATIVE_INFINITY;
    modeOffUntil = 0;
    inFlight.clear();
  },
};
